import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Dropdown } from './Components/Dropdown';
import { BellRing, Clock, MoveRight, ShoppingBag, Calendar, DollarSign, Info, AlertCircle, CheckCircle2, XCircle, Send } from 'lucide-react';
import SimpleBar from 'simplebar-react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { getAllNotificationList, updateExistingNotification } from 'slices/thunk';
import classNames from 'classnames';

interface Notification {
    id: string;
    title: string;
    message: string;
    type: string;
    category?: string;
    priority?: string;
    action_url?: string;
    action_buttons?: Array<{
        label: string;
        action: string;
        url?: string;
    }>;
    is_read: boolean;
    read_at?: string;
    is_sent: boolean;
    delivery_status?: string;
    created_at: string;
}

const NotificationDropdown = () => {
    const dispatch = useDispatch<any>();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [filter, setFilter] = useState<string>("all");
    const [categoryFilter, setCategoryFilter] = useState<string>("all");
    const [loading, setLoading] = useState<boolean>(true);
    const [hasNewNotifications, setHasNewNotifications] = useState<boolean>(false);
    const previousNotificationCount = useRef<number>(0);

    const fetchNotifications = useCallback(async (showLoading = false) => {
        try {
            if (showLoading) {
                setLoading(true);
            }
            const response = await dispatch(getAllNotificationList({}));
            let notificationData: Notification[] = [];
            if (response.error) {
                setNotifications([]);
                return;
            }
            if (response?.payload) {
                if (response.payload.notifications && Array.isArray(response.payload.notifications)) {
                    notificationData = response.payload.notifications;
                } else if (Array.isArray(response.payload)) {
                    notificationData = response.payload;
                } else {
                    notificationData = [];
                }
            } else if (response?.data) {
                notificationData = Array.isArray(response.data) ? response.data : [];
            } else if (Array.isArray(response)) {
                notificationData = response;
            } else {
                notificationData = [];
            }
            if (!Array.isArray(notificationData)) {
                notificationData = [];
            }
            const currentCount = notificationData.length;
            if (currentCount > previousNotificationCount.current && previousNotificationCount.current > 0) {
                setHasNewNotifications(true);
                setTimeout(() => setHasNewNotifications(false), 3000);
            }
            previousNotificationCount.current = currentCount;
            setNotifications(notificationData);
        } catch (error) {
            console.error('Error fetching notifications:', error);
            setNotifications([]);
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    // Single source: start polling first, use its data for initial list (no duplicate fetch on load)
    useEffect(() => {
        const startNotificationService = async () => {
            try {
                const { default: NotificationPollingService } = await import('services/notificationPollingService');
                const notificationService = NotificationPollingService.getInstance();
                if (!notificationService.getPollingStatus()) {
                    await notificationService.startPolling();
                }
                // Use already-fetched list from polling service to avoid duplicate API call
                const list = notificationService.getLastFetchedNotifications();
                if (Array.isArray(list) && list.length > 0) {
                    setNotifications(list.map((n) => ({ ...n, type: n.type ?? 'general' })) as Notification[]);
                }
                setLoading(false);
            } catch (error) {
                console.error('Error starting notification service:', error);
                setLoading(false);
            }
        };
        startNotificationService();
    }, []);

    // Sync list from notification polling service (single source; service polls at 60s+)
    useEffect(() => {
        const handleNotificationUpdate = async () => {
            try {
                const { default: NotificationPollingService } = await import('services/notificationPollingService');
                const list = NotificationPollingService.getInstance().getLastFetchedNotifications();
                if (!Array.isArray(list)) return;
                setNotifications(
                    list.map((n) => ({
                        ...n,
                        type: n.type ?? 'general',
                    })) as Notification[]
                );
                setLoading(false);
            } catch (_) {}
        };
        window.addEventListener('notificationUpdate', handleNotificationUpdate);
        return () => window.removeEventListener('notificationUpdate', handleNotificationUpdate);
    }, []);

    // Listen for login notification events (only then do a manual refresh)
    useEffect(() => {
        const handleLoginNotification = () => {
            fetchNotifications(false);
        };
        window.addEventListener('loginNotification', handleLoginNotification);
        return () => window.removeEventListener('loginNotification', handleLoginNotification);
    }, [fetchNotifications]);

    // Add click handler to dropdown trigger using event delegation
    useEffect(() => {
        const handleDropdownClick = (e: Event) => {
            const target = e.target as HTMLElement;
            if (target.closest('#notificationDropdown')) {
                // Optional: could trigger refresh only when dropdown opens, if needed
            }
        };
        document.addEventListener('click', handleDropdownClick);
        return () => document.removeEventListener('click', handleDropdownClick);
    }, []);



    // Handle notification click
    const handleNotificationClick = async (notification: Notification, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Mark as read if not already read
        if (!notification.is_read) {
            try {
                await dispatch(updateExistingNotification({
                    id: notification.id,
                    is_read: true
                }));
                
                // Update local state immediately for better UX
                setNotifications(prev => 
                    prev.map(n => 
                        n.id === notification.id 
                            ? { ...n, is_read: true }
                            : n
                    )
                );
            } catch (error) {
                console.error('Error marking notification as read:', error);
            }
        }

        // Navigate to action URL if provided
        if (notification.action_url && notification.action_url !== "#!") {
            navigate(notification.action_url);
        }
    };

    // Ensure notifications is always an array before filtering
    const safeNotifications = Array.isArray(notifications) ? notifications : [];
    
    // Filter and group notifications
    const filteredNotifications = useMemo(() => {
        let filtered = safeNotifications.filter((n) => {
            const typeMatch = filter === "all" || n.type === filter;
            const categoryMatch = categoryFilter === "all" || n.category === categoryFilter;
            return typeMatch && categoryMatch;
        });
        
        // Sort by priority then date
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        filtered.sort((a, b) => {
            const priA = priorityOrder[a.priority as keyof typeof priorityOrder] || 2;
            const priB = priorityOrder[b.priority as keyof typeof priorityOrder] || 2;
            if (priA !== priB) return priB - priA;
            return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        
        return filtered;
    }, [safeNotifications, filter, categoryFilter]);
    
    const unreadCount = safeNotifications.filter(n => !n.is_read).length;

    const getPriorityColor = (priority?: string) => {
        switch (priority) {
            case 'urgent': return 'bg-red-500';
            case 'high': return 'bg-orange-500';
            case 'medium': return 'bg-blue-500';
            case 'low': return 'bg-gray-400';
            default: return 'bg-blue-500';
        }
    };

    const getCategoryIcon = (category?: string) => {
        switch (category?.toLowerCase()) {
            case 'attendance': return <Clock className="size-4" />;
            case 'leave': return <Calendar className="size-4" />;
            case 'expense': return <DollarSign className="size-4" />;
            case 'system': return <BellRing className="size-4" />;
            default: return <Info className="size-4" />;
        }
    };

    const getDeliveryStatusIcon = (status?: string) => {
        switch (status) {
            case 'sent': return <Send className="size-3 text-green-500" />;
            case 'delivered': return <CheckCircle2 className="size-3 text-green-600" />;
            case 'failed': return <XCircle className="size-3 text-red-500" />;
            default: return <Clock className="size-3 text-gray-400" />;
        }
    };

    const handleActionButton = (notification: Notification, button: any, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (button.url) {
            navigate(button.url);
        } else if (button.action) {
            console.log('Action:', button.action, 'for notification:', notification.id);
        }
    };

    return (
        <Dropdown className="relative flex items-center h-header">
            <Dropdown.Trigger 
                type="button" 
                className="inline-flex justify-center relative items-center p-0 text-topbar-item transition-all size-[37.5px] duration-200 ease-linear bg-topbar rounded-md dropdown-toggle btn hover:bg-topbar-item-bg-hover hover:text-topbar-item-hover group-data-[topbar=dark]:bg-topbar-dark group-data-[topbar=dark]:hover:bg-topbar-item-bg-hover-dark group-data-[topbar=dark]:hover:text-topbar-item-hover-dark group-data-[topbar=brand]:bg-topbar-brand group-data-[topbar=brand]:hover:bg-topbar-item-bg-hover-brand group-data-[topbar=brand]:hover:text-topbar-item-bg-hover-brand group-data-[topbar=dark]:dark:bg-zink-700 group-data-[topbar=dark]:dark:hover:bg-zink-600 group-data-[topbar=brand]:text-topbar-item-brand group-data-[topbar=dark]:dark:hover:text-zink-50 group-data-[topbar=dark]:dark:text-zink-200 group-data-[topbar=dark]:text-topbar-item-dark" 
                id="notificationDropdown" 
                data-bs-toggle="dropdown"
            >
                <BellRing className={`inline-block size-5 stroke-1 fill-slate-100 group-data-[topbar=dark]:fill-topbar-item-bg-hover-dark group-data-[topbar=brand]:fill-topbar-item-bg-hover-brand transition-transform ${hasNewNotifications ? 'animate-bounce' : ''}`} />
                {unreadCount > 0 && (
                    <span className="absolute top-0 right-0 flex w-1.5 h-1.5">
                        <span className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-sky-400"></span>
                        <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-sky-500"></span>
                    </span>
                )}
            </Dropdown.Trigger>

            <Dropdown.Content 
                placement="right-end" 
                className="absolute z-50 ltr:text-left rtl:text-right bg-white rounded-md shadow-md !top-4 dropdown-menu min-w-[20rem] lg:min-w-[26rem] dark:bg-zink-600" 
                style={{ right: '0', left: 'auto', transform: 'translateY(54px)' }} 
                aria-labelledby="notificationDropdown"
            >
                <div className="p-4">
                    <div className="flex items-center justify-between mb-4">
                        <h6 className="text-16">
                            Notifications{" "}
                            <span className="inline-flex items-center justify-center size-5 ml-1 text-[11px] font-medium border rounded-full text-white bg-orange-500 border-orange-500">
                                {safeNotifications.length}
                            </span>
                        </h6>
                        {unreadCount > 0 && (
                            <span className="text-xs text-sky-600 bg-sky-100 px-2 py-1 rounded-full">
                                {unreadCount} unread
                            </span>
                        )}
                    </div>
                    <div className="space-y-2 mb-2">
                        <ul className="flex flex-wrap w-full p-1 text-sm font-medium text-center rounded-md filter-btns text-slate-500 bg-slate-100 nav-tabs dark:bg-zink-500 dark:text-zink-200">
                            {["all", "info", "alert", "reminder"].map((tab) => (
                                <li key={tab} className={`group grow ${filter === tab ? "active" : ""}`}>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setFilter(tab);
                                        }}
                                        className="inline-block nav-link px-1.5 w-full py-1 text-xs transition-all duration-300 ease-linear rounded-md text-slate-500 border border-transparent group-[.active]:bg-white group-[.active]:text-custom-500 hover:text-custom-500 active:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:group-[.active]:bg-zink-600 -mb-[1px]"
                                    >
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                        <ul className="flex flex-wrap w-full p-1 text-sm font-medium text-center rounded-md filter-btns text-slate-500 bg-slate-100 nav-tabs dark:bg-zink-500 dark:text-zink-200">
                            {["all", "attendance", "leave", "expense", "system"].map((cat) => (
                                <li key={cat} className={`group grow ${categoryFilter === cat ? "active" : ""}`}>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setCategoryFilter(cat);
                                        }}
                                        className="inline-block nav-link px-1.5 w-full py-1 text-xs transition-all duration-300 ease-linear rounded-md text-slate-500 border border-transparent group-[.active]:bg-white group-[.active]:text-custom-500 hover:text-custom-500 active:text-custom-500 dark:text-zink-200 dark:hover:text-custom-500 dark:group-[.active]:bg-zink-600 -mb-[1px]"
                                    >
                                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <SimpleBar className="max-h-[350px]">
                    <div className="flex flex-col gap-1">
                        {loading ? (
                            <div className="p-4 text-center text-slate-500 dark:text-zink-300">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
                                <p className="mt-2">Loading notifications...</p>
                            </div>
                        ) : filteredNotifications.length === 0 ? (
                            <div className="p-4 text-center text-slate-500 dark:text-zink-300">No notifications</div>
                        ) : (
                            (Array.isArray(filteredNotifications) ? filteredNotifications : []).map((item, index) => (
                                <div
                                    key={item.id || index}
                                    className={`w-full text-left flex gap-3 p-4 product-item hover:bg-slate-50 dark:hover:bg-zink-500 transition-colors ${!item.is_read ? 'bg-sky-50 dark:bg-sky-500/10 border-l-4 border-blue-500' : 'border-l-4 border-transparent'}`}
                                >
                                    <div className={classNames(
                                        "flex items-center justify-center size-10 rounded-md shrink-0",
                                        item.category === 'attendance' ? 'bg-blue-100' :
                                        item.category === 'leave' ? 'bg-green-100' :
                                        item.category === 'expense' ? 'bg-yellow-100' :
                                        item.category === 'system' ? 'bg-purple-100' :
                                        'bg-sky-100'
                                    )}>
                                        {getCategoryIcon(item.category)}
                                    </div>
                                    <div className="grow">
                                        <div className="flex items-start justify-between gap-2 mb-1">
                                            <h6 className={classNames("font-medium", !item.is_read && 'font-semibold')}>{item.title}</h6>
                                            <div className="flex items-center gap-1 shrink-0">
                                                {item.priority && (
                                                    <span className={classNames(
                                                        "px-1.5 py-0.5 text-[10px] font-medium rounded",
                                                        getPriorityColor(item.priority),
                                                        "text-white"
                                                    )}>
                                                        {item.priority.toUpperCase()}
                                                    </span>
                                                )}
                                                {getDeliveryStatusIcon(item.delivery_status)}
                                            </div>
                                        </div>
                                        <p className="text-sm text-slate-500 dark:text-zink-300 mb-1">
                                            <Clock className="inline-block size-3 mr-1" />
                                            {new Date(item.created_at).toLocaleString()}
                                        </p>
                                        {item.message && (
                                            <div className="mt-1 p-2 rounded bg-slate-100 text-slate-500 dark:bg-zink-500 dark:text-zink-300 text-sm">
                                                {item.message}
                                            </div>
                                        )}
                                        {item.action_buttons && item.action_buttons.length > 0 && (
                                            <div className="flex gap-1 mt-2">
                                                {item.action_buttons.map((btn: any, btnIdx: number) => (
                                                    <button
                                                        key={btnIdx}
                                                        onClick={(e) => handleActionButton(item, btn, e)}
                                                        className="px-2 py-1 text-xs bg-blue-100 text-blue-600 hover:bg-blue-200 rounded"
                                                    >
                                                        {btn.label}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={(e) => handleNotificationClick(item, e)}
                                        className="flex items-center self-start gap-2 text-xs text-slate-500 shrink-0 dark:text-zink-300"
                                    >
                                        {!item.is_read && <div className="w-1.5 h-1.5 bg-custom-500 rounded-full"></div>}
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </SimpleBar>

                <div className="flex items-center gap-2 p-4 border-t border-slate-200 dark:border-zink-500">
                    <div className="grow">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate('/notifications');
                            }}
                            className="text-sm text-custom-500 hover:underline"
                        >
                            Manage Notifications
                        </button>
                    </div>
                    <div className="shrink-0">
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate('/notifications');
                            }}
                            className="px-2 py-1.5 text-xs text-white transition-all duration-200 ease-linear btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600"
                        >
                            View All Notifications
                            <MoveRight className="inline-block size-3 ml-1" />
                        </button>
                    </div>
                </div>
            </Dropdown.Content>
        </Dropdown>
    );
};

export default NotificationDropdown;
